import { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { ConfirmModal } from '@/components/ConfirmModal';

const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const getInsecureUUID = _wg('getInsecureUUID');

interface FieldInfo {
  label: string;
  field: string;
  type: 'text';
}

const FIELDS: FieldInfo[] = [
  { label: 'Site Name', field: 'siteName', type: 'text' },
  { label: 'Location', field: 'location', type: 'text' },
];

interface ClientSitesGridProps {
  sites: any[];
  title?: string;
  isReadOnly?: boolean;
  onChange?: (newSites: any[]) => void;
}

export function ClientSitesGrid({
  sites = [],
  title = 'Sites',
  isReadOnly = false,
  onChange,
}: ClientSitesGridProps) {
  const { theme } = useTheme();
  const rootRef = useRef<HTMLDivElement>(null);

  const [editingField, setEditingField] = useState<{ uuid: string; field: string } | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [isMobile, setIsMobile] = useState(false);
  const [isTabbing, setIsTabbing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isJustStartingEdit, setIsJustStartingEdit] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    open: boolean;
    uuid: string | null;
    name: string;
  }>({ open: false, uuid: null, name: '' });

  // Refs to access latest values in event handlers without stale closures
  const editingFieldRef = useRef(editingField);
  const editValueRef = useRef(editValue);
  const isSavingRef = useRef(isSaving);
  const isJustStartingEditRef = useRef(isJustStartingEdit);
  const sitesRef = useRef(sites);
  const onChangeRef = useRef(onChange);

  useEffect(() => { editingFieldRef.current = editingField; }, [editingField]);
  useEffect(() => { editValueRef.current = editValue; }, [editValue]);
  useEffect(() => { isSavingRef.current = isSaving; }, [isSaving]);
  useEffect(() => { isJustStartingEditRef.current = isJustStartingEdit; }, [isJustStartingEdit]);
  useEffect(() => { sitesRef.current = sites; }, [sites]);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  // Check if mobile
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const checkIfMobile = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setIsMobile(window.innerWidth <= 1024), 150);
    };
    setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', checkIfMobile);
    return () => {
      window.removeEventListener('resize', checkIfMobile);
      if (timer) clearTimeout(timer);
    };
  }, []);

  // Emit helper
  const emitReplace = useCallback((newSites: any[]) => {
    onChangeRef.current?.(newSites);
  }, []);

  // Save the current edit
  const saveEdit = useCallback(() => {
    if (!editingFieldRef.current || isSavingRef.current) return;
    if (isJustStartingEditRef.current) return;

    isSavingRef.current = true;
    setIsSaving(true);

    const { uuid, field } = editingFieldRef.current;
    const val = editValueRef.current;
    const updatedSites = sitesRef.current.map((s: any) =>
      s.uuid === uuid ? { ...s, [field]: val } : s
    );
    emitReplace(updatedSites);

    editingFieldRef.current = null;
    editValueRef.current = '';
    setEditingField(null);
    setEditValue('');

    setTimeout(() => {
      isSavingRef.current = false;
      setIsSaving(false);
    }, 100);
  }, [emitReplace]);

  // Click outside handler
  useEffect(() => {
    const handleCellClickOutside = (event: MouseEvent) => {
      if (!editingFieldRef.current) return;

      const target = event.target as HTMLElement;
      const isInsideEditContainer = target.closest('.edit-container, .edit-field-mobile');

      if (!isInsideEditContainer) {
        saveEdit();
      }
    };

    document.addEventListener('click', handleCellClickOutside);
    return () => {
      document.removeEventListener('click', handleCellClickOutside);
    };
  }, [saveEdit]);

  // Focus input after edit starts
  const focusInput = useCallback(() => {
    requestAnimationFrame(() => {
      const el = rootRef.current;
      if (!el) return;
      const editContainer = el.querySelector('.edit-container, .edit-field-mobile');
      if (editContainer) {
        const input = editContainer.querySelector('.edit-input:not([type="hidden"])') as HTMLInputElement;
        if (input) {
          if (window.innerWidth <= 1024) {
            setTimeout(() => {
              input.focus();
              input.click();
            }, 150);
          } else {
            input.focus();
            if (input.type === 'text') {
              input.select();
            }
          }
        }
      }
    });
  }, []);

  const isEditing = useCallback((siteUuid: string, field: string): boolean => {
    return editingField?.uuid === siteUuid && editingField?.field === field;
  }, [editingField]);

  const getDisplayValue = useCallback((site: any, field: string): string => {
    const value = site[field];
    return value != null && value !== '' ? String(value) : '\u2014'; // em-dash
  }, []);

  const getHeaderWidth = useCallback((field: string): React.CSSProperties => {
    const widths: Record<string, string> = {
      'siteName': '45%',
      'location': '45%',
    };
    return { width: widths[field] || 'auto' };
  }, []);

  const startEdit = useCallback((siteUuid: string, field: string, currentValue: any) => {
    if (isReadOnly) return;

    // Save any existing edit first
    if (editingFieldRef.current &&
        (editingFieldRef.current.uuid !== siteUuid || editingFieldRef.current.field !== field)) {
      saveEdit();
    }

    const newEditing = { uuid: siteUuid, field };
    editingFieldRef.current = newEditing;
    editValueRef.current = currentValue || '';
    setEditingField(newEditing);
    setEditValue(currentValue || '');
    focusInput();
  }, [isReadOnly, saveEdit, focusInput]);

  const startEditMobile = useCallback((siteUuid: string, field: string, currentValue: any) => {
    if (isReadOnly) return;

    // Save any existing edit first
    if (editingFieldRef.current &&
        (editingFieldRef.current.uuid !== siteUuid || editingFieldRef.current.field !== field)) {
      saveEdit();
    }

    isJustStartingEditRef.current = true;
    setIsJustStartingEdit(true);

    const newEditing = { uuid: siteUuid, field };
    editingFieldRef.current = newEditing;
    editValueRef.current = currentValue || '';
    setEditingField(newEditing);
    setEditValue(currentValue || '');

    setTimeout(() => {
      isJustStartingEditRef.current = false;
      setIsJustStartingEdit(false);
    }, 200);
  }, [isReadOnly, saveEdit]);

  const cancelEdit = useCallback(() => {
    editingFieldRef.current = null;
    editValueRef.current = '';
    setEditingField(null);
    setEditValue('');
  }, []);

  const getNextEditableField = useCallback((currentUuid: string, currentField: string) => {
    const editableFields = FIELDS.map(f => f.field);
    const currentSiteIndex = sitesRef.current.findIndex((s: any) => s.uuid === currentUuid);
    const currentFieldIndex = editableFields.indexOf(currentField);

    // Move to next field in same site
    if (currentFieldIndex < editableFields.length - 1) {
      return { uuid: currentUuid, field: editableFields[currentFieldIndex + 1] };
    }

    // Move to first field in next site
    if (currentSiteIndex < sitesRef.current.length - 1) {
      const nextSite = sitesRef.current[currentSiteIndex + 1];
      return { uuid: nextSite.uuid, field: editableFields[0] };
    }

    return null;
  }, []);

  const handleTabKey = useCallback((event: React.KeyboardEvent) => {
    if (!editingFieldRef.current) return;

    event.preventDefault();
    event.stopPropagation();

    setIsTabbing(true);
    const currentField = { ...editingFieldRef.current };

    saveEdit();

    requestAnimationFrame(() => {
      const next = getNextEditableField(currentField.uuid, currentField.field);
      if (next) {
        const site = sitesRef.current.find((s: any) => s.uuid === next.uuid);
        if (site) {
          startEdit(next.uuid, next.field, site[next.field]);
        }
      }
      setIsTabbing(false);
    });
  }, [saveEdit, getNextEditableField, startEdit]);

  const addSite = useCallback(() => {
    if (isReadOnly) return;

    const newSite = {
      uuid: getInsecureUUID(),
      id: null,
      siteName: '',
      location: '',
    };
    emitReplace([...sitesRef.current, newSite]);
  }, [isReadOnly, emitReplace]);

  const openDeleteConfirm = useCallback((uuid: string) => {
    if (isReadOnly) return;

    const site = sitesRef.current.find((s: any) => s.uuid === uuid);
    setDeleteConfirm({
      open: true,
      uuid,
      name: site ? site.siteName || 'this site' : 'this site',
    });
  }, [isReadOnly]);

  const confirmDelete = useCallback(() => {
    const uuid = deleteConfirm.uuid;
    if (!uuid) {
      setDeleteConfirm({ open: false, uuid: null, name: '' });
      return;
    }
    emitReplace(sitesRef.current.filter((s: any) => s.uuid !== uuid));
    setDeleteConfirm({ open: false, uuid: null, name: '' });
  }, [deleteConfirm.uuid, emitReplace]);

  const cancelDelete = useCallback(() => {
    setDeleteConfirm({ open: false, uuid: null, name: '' });
  }, []);

  // Render an editable cell for the desktop table
  const renderEditableCell = (site: any, fieldInfo: FieldInfo) => {
    if (isEditing(site.uuid, fieldInfo.field)) {
      return (
        <div className="edit-container">
          <input
            value={editValue}
            onChange={(e) => {
              editValueRef.current = e.target.value;
              setEditValue(e.target.value);
            }}
            className="edit-input"
            onBlur={saveEdit}
            onKeyUp={(e) => {
              if (e.key === 'Enter') saveEdit();
              if (e.key === 'Escape') cancelEdit();
            }}
            onKeyDown={(e) => { if (e.key === 'Tab') handleTabKey(e); }}
          />
        </div>
      );
    }

    return (
      <div
        className="editable"
        onClick={(e) => {
          e.stopPropagation();
          startEdit(site.uuid, fieldInfo.field, site[fieldInfo.field]);
        }}
      >
        {getDisplayValue(site, fieldInfo.field)}
      </div>
    );
  };

  // Render a mobile field
  const renderMobileField = (site: any, fieldInfo: FieldInfo) => {
    if (isEditing(site.uuid, fieldInfo.field)) {
      return (
        <div className="edit-field-mobile">
          <input
            value={editValue}
            onChange={(e) => {
              editValueRef.current = e.target.value;
              setEditValue(e.target.value);
            }}
            className="edit-input"
            autoFocus
            onBlur={saveEdit}
            onKeyUp={(e) => {
              if (e.key === 'Enter') saveEdit();
              if (e.key === 'Escape') cancelEdit();
            }}
          />
        </div>
      );
    }

    return (
      <div
        className="card-field-value"
        onClick={() => {
          startEditMobile(site.uuid, fieldInfo.field, site[fieldInfo.field]);
        }}
      >
        {getDisplayValue(site, fieldInfo.field)}
      </div>
    );
  };

  return (
    <div className={`client-group-sites ${theme}`} ref={rootRef}>
      <div className="header">
        <h1>{title}</h1>
        {!isReadOnly && (
          <button className="btn-add" onClick={addSite}>
            <i className="fas fa-plus"></i> SITE
          </button>
        )}
      </div>

      {/* Desktop Table View */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              {FIELDS.map((fieldInfo) => (
                <th key={fieldInfo.field} style={getHeaderWidth(fieldInfo.field)}>
                  {fieldInfo.label}
                </th>
              ))}
              <th style={{ width: '10%' }}>Actions</th>
            </tr>
          </thead>
          {sites.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={3} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                  No sites configured
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {sites.map((site: any) => (
                <tr key={site.uuid}>
                  {FIELDS.map((fieldInfo) => (
                    <td key={fieldInfo.field}>
                      {renderEditableCell(site, fieldInfo)}
                    </td>
                  ))}
                  <td>
                    {!isReadOnly && (
                      <div className="actions">
                        <button className="btn-delete" onClick={() => openDeleteConfirm(site.uuid)}>
                          Delete
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>

      {/* Mobile Cards View */}
      <div className="mobile-cards">
        {sites.length === 0 && (
          <div className="empty-state">
            <p>No sites configured</p>
          </div>
        )}

        {sites.map((site: any) => (
          <div key={'mobile-' + site.uuid} className="card">
            <div className="card-header">
              <div className="card-header-content">
                <div className="card-site-name">
                  {site.siteName || 'New Site'}
                </div>
              </div>
              {!isReadOnly && (
                <button className="btn-delete" onClick={() => openDeleteConfirm(site.uuid)}>
                  Delete
                </button>
              )}
            </div>

            <div className="card-grid">
              {FIELDS.map((fieldInfo) => (
                <div key={fieldInfo.field} className="card-field">
                  <div className="card-field-label">{fieldInfo.label}</div>
                  {renderMobileField(site, fieldInfo)}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={deleteConfirm.open}
        title={`Remove ${deleteConfirm.name}?`}
        confirmText="Remove"
        cancelText="Cancel"
        autoFocus="cancel"
        scrollLock={true}
        theme={theme}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
}

(window as any).ClientSitesGridReact = ClientSitesGrid;
